/**
 * pricing.operations.ts — the PRICING document registry (CU-1b, fullsuite
 * plan §B2.5'/§B2.7'; extends the taxonomy + masters seam pattern).
 *
 * The document TEXTS are single-sourced from the contract pack
 * (`packages/contracts/operations/*.graphql`) via vite `?raw` imports, so the
 * facade can never drift from the reviewed contract text. Every document here
 * is machine-validated against the FROZEN SDL snapshot by
 * `scripts/validate-operations.mjs` — and that gate is exactly what proved the
 * three B0.7' pricing PAGE documents (`PricingSetupPage` /
 * `PricingSettingsPage` / `PricingSimulatorPage`) cite a page-shaped surface
 * B2.5' never built. This registry therefore adopts PER-COLLECTION documents,
 * one per facade method, and leaves the three page documents AHEAD.
 *
 * INTERIM (masters/taxonomy precedent; removal trigger: pricing adoption in
 * codegen.ts): the hand-declared TYPES below are not generated
 * `TypedDocumentNode` re-exports. They ride plain full-text POSTs
 * (graphql.client.ts null-hash rule). The projections mirror the operation
 * fragments field-for-field; drift is guarded by the pricingSeam vitest.
 *
 * WIRE STANCE. `Money` is a canonical decimal STRING on the wire (ADR-008 —
 * no float ever leaves the server); the gql-map parses it to the `number` the
 * legacy REST types declare, at the ONE boundary. Percentages are `Float`
 * (ratios, not amounts) and cross unchanged. Enums arrive SCREAMING_SNAKE and
 * the gql-map lowercases them back to the legacy vocabulary — a table, never
 * a `toLowerCase()`, so an unknown member fails loudly instead of silently
 * inventing a legacy value.
 */

import pricingResolveText from '../../../packages/contracts/operations/PricingResolve.graphql?raw';
import pricingPriceLevelsText from '../../../packages/contracts/operations/PricingPriceLevels.graphql?raw';
import pricingPriceGroupBandsText from '../../../packages/contracts/operations/PricingPriceGroupBands.graphql?raw';
import pricingMarkupRulesText from '../../../packages/contracts/operations/PricingMarkupRules.graphql?raw';
import pricingSpecialItemPricesText from '../../../packages/contracts/operations/PricingSpecialItemPrices.graphql?raw';
import pricingCustomerAssignmentsText from '../../../packages/contracts/operations/PricingCustomerAssignments.graphql?raw';
import pricingCostRecordsText from '../../../packages/contracts/operations/PricingCostRecords.graphql?raw';
import pricingQuantitySchedulesText from '../../../packages/contracts/operations/PricingQuantitySchedules.graphql?raw';
import pricingGroupsListText from '../../../packages/contracts/operations/PricingGroupsList.graphql?raw';
import pricingCustomerGroupDiscountsText from '../../../packages/contracts/operations/PricingCustomerGroupDiscounts.graphql?raw';
import pricingCurrencyPriceRowsText from '../../../packages/contracts/operations/PricingCurrencyPriceRows.graphql?raw';
import pricingProductPricingFieldsText from '../../../packages/contracts/operations/PricingProductPricingFields.graphql?raw';
import pricingExchangeRatesText from '../../../packages/contracts/operations/PricingExchangeRates.graphql?raw';
import priceLevelSaveText from '../../../packages/contracts/operations/PriceLevelSave.graphql?raw';
import priceGroupBandSaveText from '../../../packages/contracts/operations/PriceGroupBandSave.graphql?raw';
import pricingGroupSaveText from '../../../packages/contracts/operations/PricingGroupSave.graphql?raw';
import markupRulesSaveText from '../../../packages/contracts/operations/MarkupRulesSave.graphql?raw';
import specialItemPriceSaveText from '../../../packages/contracts/operations/SpecialItemPriceSave.graphql?raw';
import specialItemPriceSetActiveText from '../../../packages/contracts/operations/SpecialItemPriceSetActive.graphql?raw';
import customerPriceAssignmentSaveText from '../../../packages/contracts/operations/CustomerPriceAssignmentSave.graphql?raw';
import costRecordPatchText from '../../../packages/contracts/operations/CostRecordPatch.graphql?raw';
import quantityPricingScheduleSaveText from '../../../packages/contracts/operations/QuantityPricingScheduleSave.graphql?raw';
import customerGroupDiscountSaveText from '../../../packages/contracts/operations/CustomerGroupDiscountSave.graphql?raw';
import currencyPriceRowSaveText from '../../../packages/contracts/operations/CurrencyPriceRowSave.graphql?raw';
import productPricingFieldsSaveText from '../../../packages/contracts/operations/ProductPricingFieldsSave.graphql?raw';

/** Operation names — must equal each file's basename (safelist identity). */
export const PRICING_OPERATION_NAMES = {
  resolve: 'PricingResolve',
  priceLevels: 'PricingPriceLevels',
  priceGroupBands: 'PricingPriceGroupBands',
  markupRules: 'PricingMarkupRules',
  specialItemPrices: 'PricingSpecialItemPrices',
  customerAssignments: 'PricingCustomerAssignments',
  costRecords: 'PricingCostRecords',
  quantitySchedules: 'PricingQuantitySchedules',
  pricingGroups: 'PricingGroupsList',
  customerGroupDiscounts: 'PricingCustomerGroupDiscounts',
  currencyPriceRows: 'PricingCurrencyPriceRows',
  productPricingFields: 'PricingProductPricingFields',
  exchangeRates: 'PricingExchangeRates',
  priceLevelSave: 'PriceLevelSave',
  priceGroupBandSave: 'PriceGroupBandSave',
  pricingGroupSave: 'PricingGroupSave',
  markupRulesSave: 'MarkupRulesSave',
  specialItemPriceSave: 'SpecialItemPriceSave',
  specialItemPriceSetActive: 'SpecialItemPriceSetActive',
  customerPriceAssignmentSave: 'CustomerPriceAssignmentSave',
  costRecordPatch: 'CostRecordPatch',
  quantityPricingScheduleSave: 'QuantityPricingScheduleSave',
  customerGroupDiscountSave: 'CustomerGroupDiscountSave',
  currencyPriceRowSave: 'CurrencyPriceRowSave',
  productPricingFieldsSave: 'ProductPricingFieldsSave',
} as const;

export const pricingOperations = {
  resolve: pricingResolveText,
  priceLevels: pricingPriceLevelsText,
  priceGroupBands: pricingPriceGroupBandsText,
  markupRules: pricingMarkupRulesText,
  specialItemPrices: pricingSpecialItemPricesText,
  customerAssignments: pricingCustomerAssignmentsText,
  costRecords: pricingCostRecordsText,
  quantitySchedules: pricingQuantitySchedulesText,
  pricingGroups: pricingGroupsListText,
  customerGroupDiscounts: pricingCustomerGroupDiscountsText,
  currencyPriceRows: pricingCurrencyPriceRowsText,
  productPricingFields: pricingProductPricingFieldsText,
  exchangeRates: pricingExchangeRatesText,
  priceLevelSave: priceLevelSaveText,
  priceGroupBandSave: priceGroupBandSaveText,
  pricingGroupSave: pricingGroupSaveText,
  markupRulesSave: markupRulesSaveText,
  specialItemPriceSave: specialItemPriceSaveText,
  specialItemPriceSetActive: specialItemPriceSetActiveText,
  customerPriceAssignmentSave: customerPriceAssignmentSaveText,
  costRecordPatch: costRecordPatchText,
  quantityPricingScheduleSave: quantityPricingScheduleSaveText,
  customerGroupDiscountSave: customerGroupDiscountSaveText,
  currencyPriceRowSave: currencyPriceRowSaveText,
  productPricingFieldsSave: productPricingFieldsSaveText,
} as const;

// ---------------------------------------------------------------------------
// Graph shapes — mirror the operation selections field-for-field
// ---------------------------------------------------------------------------

/** ADR-008 canonical decimal string. Never a float on the wire. */
export type GqlMoney = string;

export interface GqlUserError {
  code: string;
  field: string | null;
  message: string;
}

export interface GqlPriceLevel {
  id: string;
  code: string;
  nameEn: string;
  nameZh: string;
  sortOrder: number;
  isActive: boolean;
}

export interface GqlPriceGroupBand {
  id: string;
  group: string;
  minInclusive: GqlMoney;
  maxExclusive: GqlMoney | null;
  currency: string;
  /** NON-NULL on the wire: the graph answers the domain's total rule. */
  isActive: boolean;
}

export interface GqlMarkupRule {
  id: string;
  priceGroupId: string;
  source: string;
  priceLevelId: string;
  markupPct: number;
}

export interface GqlSpecialItemPrice {
  id: string;
  customerId: number;
  productId: string;
  kind: string;
  fixedPrice: GqlMoney | null;
  currency: string | null;
  markupPct: number | null;
  /**
   * `PricingAnchor` member — the regime a MARKUP_PCT row was authored against.
   * Nullable on the wire because the FIXED arm has no such question; on a
   * MARKUP_PCT row the gql-map REFUSES a null rather than inventing a base.
   */
  basisAtAuthoring: string | null;
  reason: string;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

export interface GqlCustomerPriceAssignment {
  customerId: number;
  priceLevelId: string | null;
  discountPct: number | null;
  perCategoryPriceLevels: Array<{ categoryId: string; priceLevelId: string }>;
}

export interface GqlCostRecord {
  id: string;
  productId: string;
  source: string;
  basis: string;
  purchaseCost: GqlMoney;
  landedAllocation: GqlMoney;
  internalMarkupPct: number;
  salesMarkupPct: number;
  currency: string;
  effectiveDate: string;
  trackLandedCost: boolean;
  landedCostAllocationBasis: string | null;
  fxSnapshot: {
    fromCurrency: string;
    toCurrency: string;
    rate: number;
    capturedAt: string;
  } | null;
}

export interface GqlQuantityBracket {
  minQuantity: number;
  discountPct: number;
}

export interface GqlQuantityPricingSchedule {
  id: string;
  code: string;
  nameEn: string;
  nameZh: string;
  basis: string;
  isActive: boolean;
  brackets: GqlQuantityBracket[];
}

export interface GqlPricingGroup {
  id: string;
  code: string;
  nameEn: string;
  nameZh: string;
  categoryId: string | null;
  defaultDiscountPct: number | null;
  isActive: boolean;
}

export interface GqlCustomerGroupDiscount {
  id: string;
  customerId: number;
  groupId: string;
  kind: string;
  discountPct: number | null;
  priceLevelId: string | null;
}

export interface GqlCurrencyPriceRow {
  id: string;
  productId: string;
  priceLevelId: string | null;
  currency: string;
  price: GqlMoney;
  fixedPrice: boolean;
}

export interface GqlProductPricingFields {
  productId: string;
  pricingGroupId: string | null;
  quantityScheduleId: string | null;
  costEstimateType: string | null;
  quantityBrackets: GqlQuantityBracket[];
}

export interface GqlExchangeRate {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  effectiveDate: string;
}

export interface GqlPriceTraceEntry {
  tier: string;
  matched: boolean;
  won: boolean;
  candidatePrice: GqlMoney | null;
  ruleRef: string | null;
  skipReason: string | null;
}

export interface GqlPriceDiscountTraceEntry {
  stage: string;
  matched: boolean;
  candidatePrice: GqlMoney | null;
  discountPct: number | null;
  ruleRef: string | null;
  skipReason: string | null;
}

export interface GqlPriceWaterfallRow {
  key: string;
  labelKey: string;
  formula: string | null;
  amount: GqlMoney;
}

export interface GqlPriceResolution {
  unitPrice: GqlMoney | null;
  currency: string | null;
  winner: string;
  /** `PricingAnchor` member — NON-NULL: every resolution names the cost it used. */
  pricingAnchor: string;
  asOf: string;
  currencySkipReason: string | null;
  trace: GqlPriceTraceEntry[];
  discountTrace: GqlPriceDiscountTraceEntry[] | null;
  waterfall: GqlPriceWaterfallRow[];
  marginVsLanded: { amount: GqlMoney; pct: number } | null;
}

// --- result envelopes -------------------------------------------------------

export interface PricingResolveResult {
  resolvePrice: GqlPriceResolution;
}
export interface PriceLevelsResult {
  priceLevels: GqlPriceLevel[];
}
export interface PriceGroupBandsResult {
  priceGroupBands: GqlPriceGroupBand[];
}
export interface MarkupRulesResult {
  markupRules: GqlMarkupRule[];
}
export interface SpecialItemPricesResult {
  specialItemPrices: GqlSpecialItemPrice[];
}
export interface CustomerAssignmentsResult {
  customerPriceAssignments: GqlCustomerPriceAssignment[];
}
export interface CostRecordsResult {
  costRecords: GqlCostRecord[];
}
export interface QuantitySchedulesResult {
  quantityPricingSchedules: GqlQuantityPricingSchedule[];
}
export interface PricingGroupsResult {
  pricingGroups: GqlPricingGroup[];
}
export interface CustomerGroupDiscountsResult {
  customerGroupDiscounts: GqlCustomerGroupDiscount[];
}
export interface CurrencyPriceRowsResult {
  currencyPriceRows: GqlCurrencyPriceRow[];
}
export interface ProductPricingFieldsResult {
  productPricingFields: GqlProductPricingFields[];
}
export interface ExchangeRatesResult {
  exchangeRates: GqlExchangeRate[];
}

/**
 * Every admin mutation answers the SAME payload shape; each document selects
 * only the collection it touched, so the others arrive `undefined`.
 */
export interface GqlPricingAdminPayload {
  priceLevels?: GqlPriceLevel[] | null;
  priceGroupBands?: GqlPriceGroupBand[] | null;
  markupRules?: GqlMarkupRule[] | null;
  specialItemPrices?: GqlSpecialItemPrice[] | null;
  customerPriceAssignments?: GqlCustomerPriceAssignment[] | null;
  costRecords?: GqlCostRecord[] | null;
  quantityPricingSchedules?: GqlQuantityPricingSchedule[] | null;
  pricingGroups?: GqlPricingGroup[] | null;
  customerGroupDiscounts?: GqlCustomerGroupDiscount[] | null;
  currencyPriceRows?: GqlCurrencyPriceRow[] | null;
  productPricingFields?: GqlProductPricingFields[] | null;
  audit?: {
    entityType: string;
    entityId: string;
    summary: string;
    reason: string;
  } | null;
  userErrors: GqlUserError[];
}

/** One key per mutation root — the document names its own field. */
export type PricingAdminResult = Record<string, GqlPricingAdminPayload | undefined>;
